// ComplianceContent.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, ChevronDown, ChevronUp } from "lucide-react"; // Ensure this is installed via `npm install lucide-react`
import "../styles/RegulatoryCompliance.css";
import Advisory from "./AdvisoryProcessFlow";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0 },
};

const services = [
  {
    title: "Strategic Planning & Business Advisory",
    description:
      "We craft clear, actionable roadmaps that align vision with measurable outcomes.",
          lists: [
        "Strategic Plan Document (3–5 year roadmap with goals, objectives, milestones)",
        "Vision, Mission & Values refinement.",
        "Implementation Roadmap (timelines, responsibilities, KPIs).",
        "Decision-making Frameworks & Governance structures",
        "Executive Presentations for leadership buy-in",
      ],
  },
  {
    title: "Market Entry & Expansion",
    description:
      "We help businesses assess opportunities and design winning strategies to scale locally and across Africa.",
          lists: [
        "Market Research & Opportunity Assessment Report",
        "Competitive Landscape & Risk Assessment",
        "Market Entry / Expansion Strategy Document",
        "Regulatory & Compliance Checklist",
        "Go-to-Market (GTM) Plan",
        "Partnership & Distribution Strategy",
        "Country/Regional Expansion Playbook",
      ],
  },
  {
    title: "Business Model Innovation",
    description:
      "We reimagine and optimize business models for relevance, profitability, and resilience.",
          lists: [
        "Current Business Model Audit Report",
        "Business Model Canvas / Lean Canvas (optimized version)",
        "New/Improved Revenue Stream Mapping",
        "Cost Structure Optimization Plan",
        "Profitability & Scalability Analysis",
      ],
  },
  {
    title: "Feasibility Studies & Investment Decks",
    description:
      "We evaluate project viability and prepare ventures for investor and funder engagement.",
          lists: [
        "Feasibility Study Report (market, technical, financial, legal, operational)",
        "Financial Models (cash flow, NPV, IRR, breakeven analysis)",
        "Business Case Report",
        "Investment Deck (pitch-ready slides for investors/funders)",
        "Investor-focused Executive Summary (2–3 pages)",
        "Risk Analysis & Mitigation Framework",
      ],
      paragraph: "This foundation ensures you move from idea to action with clarity and speed.",
  },
  {
    title: "Operational Efficiency & KPI Design",
    description:
      "We strengthen operations and embed performance measurement tools for accountability and growth.",
          lists: [
        "Operations Assessment Report (process audit, gap analysis)",
        "Process Optimization Framework (SOPs, workflow redesign)",
        "KPI Dashboard (Excel/Power BI enabled)",
        "Balanced Scorecard & Performance Measurement Tools",
        "Cost Reduction & Productivity Improvement Plan",
        "Monitoring & Evaluation (M&E) Framework",
      ],
  },
  {
    title: "ESG & Sustainability Strategy",
    description:
      "We integrate sustainability and ESG principles into business models to drive impact and compliance.",
          lists: [
        "ESG/Sustainability Assessment Report",
        "Materiality Matrix (prioritizing ESG issues for business & stakeholders)",
        "ESG Roadmap & Action Plan",
        "Sustainability Policy & Reporting Framework (GRI, SDGs, etc.)",
        "Stakeholder Engagement Plan",
        "Impact Measurement & Reporting Templates",
      ],
  },
];

const ComplianceContent = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <>
      <motion.div
        className="compliance-content"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="partner-image">
          <div className="partner-overlay">
            <h2 className="overlay-head">Nexa Partners</h2>
            <h3 className="italic-text">
              <em>Your Strategic Advisory Ally</em>
            </h3>
          </div>
        </div>

        <div className="compliance-text">
          <p>
            At Nexa Partners, we help startups, SMEs, NGOs, and corporates make data-driven decisions, expand into new markets, improve operations, and achieve sustainable growth.
          </p>
          <p>Our role is more than just consultancy — we become a trusted partner, equipping organizations with strategies, frameworks, and tools to navigate complexity, seize opportunities, and deliver measurable results.</p>
        </div>
              <h2 className="core-head">Core Service Lines & Deliverables</h2>

        <motion.div
          className="services-list"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="service-card"
              variants={itemVariants}
            >
              <div className="icon-text">
                <div className="icon-wrap">
                  <CheckCircle size={24} className="content-check-icon" />
                </div>
                <div className="service-text-content">
                  <h4 onClick={() => setExpandedIndex(expandedIndex === index ? null : index)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {service.title}
                    {expandedIndex === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </h4>
                  {expandedIndex === index && <p>{service.description}</p>}
                  {expandedIndex === index && <h2 className="core-head">Deliverables:</h2>}
                  {expandedIndex === index && service.lists && (
                    <ul className="service-listing">
                      {service.lists.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
              <p><strong>We are not just advisors — we are your strategic ally. </strong> Together, we design strategies that inspire action, unlock growth, and deliver measurable impact.</p>
      </motion.div>

      {/* Advisory component is now outside the compliance-content container */}
      <Advisory />
    </>
  );
};

export default ComplianceContent;
